import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Shield, Sparkles, FileText, Check } from 'lucide-react';
import Header from './Header';

// Using relative URLs

const FRAMEWORKS = [
  { id: 'SOC 2', name: 'SOC 2', description: 'Service Organization Control 2' },
  { id: 'ISO 27001', name: 'ISO 27001', description: 'Information Security Management' },
  { id: 'GDPR', name: 'GDPR', description: 'General Data Protection Regulation' },
  { id: 'DPDP', name: 'DPDP', description: 'Digital Personal Data Protection' },
  { id: 'PCI DSS', name: 'PCI DSS', description: 'Payment Card Industry Data Security' }
];

const PolicyWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [generatedPolicies, setGeneratedPolicies] = useState([]);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [customPolicies, setCustomPolicies] = useState([]);

  const handleFrameworkToggle = (frameworkId) => {
    if (selectedFrameworks.includes(frameworkId)) {
      setSelectedFrameworks(selectedFrameworks.filter(f => f !== frameworkId));
    } else {
      setSelectedFrameworks([...selectedFrameworks, frameworkId]);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleGeneratePolicyList = async () => {
    if (selectedFrameworks.length === 0) {
      toast.error('Please select at least one framework');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `/api/policies/generate-list`,
        {
          frameworks: selectedFrameworks
        },
        getAuthHeaders()
      );
      
      setGeneratedPolicies(response.data.policies || []);
      setSelectedPolicies(response.data.policies.map((_, index) => index)); // Select all by default
      setStep(2);
      toast.success('Policy list generated!');
    } catch (error) {
      toast.error('Failed to generate policy list');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyToggle = (index) => {
    if (selectedPolicies.includes(index)) {
      setSelectedPolicies(selectedPolicies.filter(i => i !== index));
    } else {
      setSelectedPolicies([...selectedPolicies, index]);
    }
  };

  const handleAddCustomPolicy = () => {
    setCustomPolicies([...customPolicies, { name: '', description: '' }]);
  };

  const handleCustomPolicyChange = (index, field, value) => {
    const updated = [...customPolicies];
    updated[index][field] = value;
    setCustomPolicies(updated);
  };

  const handleRemoveCustomPolicy = (index) => {
    setCustomPolicies(customPolicies.filter((_, i) => i !== index));
  };

  const handleCreatePolicies = async () => {
    const policiesToCreate = [
      ...selectedPolicies.map(index => generatedPolicies[index]),
      ...customPolicies.filter(p => p.name.trim())
    ];

    if (policiesToCreate.length === 0) {
      toast.error('Please select or add at least one policy');
      return;
    }

    setLoading(true);
    try {
      // Pull the signed-in user's company name so the AI generator has
      // the right organisation context. If unset, generation will fail
      // with 400 — surface a clear message before we create anything.
      let userCompany = '';
      try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        userCompany = u?.company_name || '';
      } catch { /* no-op */ }
      if (!userCompany.trim()) {
        toast.error('Set your company name on your Profile before creating policies — AI drafting needs it for context.');
        setLoading(false);
        return;
      }

      // For each requested policy: (1) draft the AI content FIRST, then
      // (2) create the policy with that content already populated. Doing
      // it in this order means the v1 snapshot ends up with the real AI
      // content — clicking "Version 1" in the version dropdown shows the
      // actual policy text instead of the "content is being generated by
      // AI" placeholder. Failures on generation are non-fatal per policy:
      // we still create the shell so the user can retry from the detail
      // page.
      let generationFailures = 0;
      const results = await Promise.all(
        policiesToCreate.map(async (policy) => {
          let content = null;
          try {
            const genRes = await axios.post(
              `/api/policies/generate-content`,
              {
                policy_name: policy.name,
                description: policy.description || '',
                frameworks: selectedFrameworks,
                company_name: userCompany,
              },
              getAuthHeaders(),
            );
            content = genRes.data?.content || null;
            if (!content) generationFailures += 1;
          } catch (e) {
            generationFailures += 1;
            console.warn('AI draft generation failed for policy', policy.name, e);
          }
          return axios.post(
            `/api/policies`,
            {
              name: policy.name,
              description: policy.description,
              frameworks: selectedFrameworks,
              content,
            },
            getAuthHeaders(),
          );
        }),
      );

      if (generationFailures === 0) {
        toast.success(`${results.length} policies created with AI drafts.`);
      } else if (generationFailures < results.length) {
        toast.warning(
          `${results.length} policies created. AI drafting failed for ${generationFailures} — retry from the policy page.`,
        );
      } else {
        toast.warning(
          `${results.length} policies created — AI drafting failed. Retry from each policy page.`,
        );
      }
      navigate('/policies');
    } catch (error) {
      toast.error('Failed to create policies');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => step === 1 ? navigate('/policies') : setStep(step - 1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 1 ? 'Back to Policies' : 'Previous Step'}
        </Button>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${step >= 1 ? 'text-navy-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-navy-600 text-white' : 'bg-slate-200'}`}>
                {step > 1 ? <Check className="w-5 h-5" /> : '1'}
              </div>
              <span className="ml-2 font-medium">Select Frameworks</span>
            </div>
            <div className="flex-1 h-1 mx-4 bg-slate-200">
              <div className={`h-full ${step >= 2 ? 'bg-navy-600' : 'bg-slate-200'}`} style={{width: step >= 2 ? '100%' : '0%'}}></div>
            </div>
            <div className={`flex items-center ${step >= 2 ? 'text-navy-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-navy-600 text-white' : 'bg-slate-200'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Select Policies</span>
            </div>
          </div>
        </div>

        {/* Step 1: Select Frameworks */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-6 h-6 mr-2 text-navy-600" />
                Select Compliance Frameworks
              </CardTitle>
              <CardDescription>
                Choose one or more frameworks to generate relevant policies
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FRAMEWORKS.map((framework) => (
                  <div
                    key={framework.id}
                    onClick={() => handleFrameworkToggle(framework.id)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedFrameworks.includes(framework.id)
                        ? 'border-navy-600 bg-emerald-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{framework.name}</h3>
                        <p className="text-sm text-slate-600 mt-1">{framework.description}</p>
                      </div>
                      {selectedFrameworks.includes(framework.id) && (
                        <Check className="w-5 h-5 text-navy-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-6">
                <Button
                  onClick={handleGeneratePolicyList}
                  disabled={loading || selectedFrameworks.length === 0}
                  className="w-full bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {loading ? 'Generating Policy List...' : 'Generate Policy List with AI'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select/Customize Policies */}
        {step === 2 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-6 h-6 mr-2 text-navy-600" />
                  AI-Generated Policies
                </CardTitle>
                <CardDescription>
                  Select the policies you want to create. Click to toggle selection.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-3">
                {generatedPolicies.map((policy, index) => (
                  <div
                    key={index}
                    onClick={() => handlePolicyToggle(index)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedPolicies.includes(index)
                        ? 'border-navy-600 bg-emerald-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{policy.name}</h3>
                        <p className="text-sm text-slate-600 mt-1">{policy.description}</p>
                        <div className="flex gap-2 mt-2">
                          {selectedFrameworks.map(fw => (
                            <Badge key={fw} variant="outline" className="text-xs">
                              {fw}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {selectedPolicies.includes(index) && (
                        <Check className="w-5 h-5 text-navy-600 ml-4" />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Custom Policies Section */}
            <Card>
              <CardHeader>
                <CardTitle>Add Custom Policies</CardTitle>
                <CardDescription>
                  Manually add policies that aren't in the AI-generated list
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {customPolicies.map((policy, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-start">
                      <Label className="text-sm font-medium">Custom Policy #{index + 1}</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCustomPolicy(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove
                      </Button>
                    </div>
                    <Input
                      placeholder="Policy Name"
                      value={policy.name}
                      onChange={(e) => handleCustomPolicyChange(index, 'name', e.target.value)}
                    />
                    <Textarea
                      placeholder="Policy Description"
                      value={policy.description}
                      onChange={(e) => handleCustomPolicyChange(index, 'description', e.target.value)}
                      rows={2}
                    />
                  </div>
                ))}

                <Button
                  variant="outline"
                  onClick={handleAddCustomPolicy}
                  className="w-full"
                >
                  + Add Custom Policy
                </Button>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleCreatePolicies}
                disabled={loading || (selectedPolicies.length === 0 && customPolicies.filter(p => p.name.trim()).length === 0)}
                className="flex-1 bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
              >
                {loading ? 'Creating...' : 'Create Selected Policies'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyWizard;
